import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pensamientos Bíblicos
 * - Admin: ve el candidato pendiente + botones Editar/Aprobar/Rechazar/Refrescar, y el activo actual.
 * - Estudiante/Profesor/Pastor/Graduado: ve el activo actual (con "Fuente: BibleTalk.tv").
 * Solo hay un activo a la vez (se sobreescribe al aprobar).
 */
const PensamientosBiblicos = ({ user }) => {
  const [activo, setActivo] = useState(null);
  const [candidato, setCandidato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refrescando, setRefrescando] = useState(false);
  const [aprobando, setAprobando] = useState(false);
  const [rechazando, setRechazando] = useState(false);
  const [retirando, setRetirando] = useState(false);
  // Propuestas propias
  const [propuestasAdmin, setPropuestasAdmin] = useState([]);
  const [misPropuestas, setMisPropuestas] = useState([]);
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [enviandoPropuesta, setEnviandoPropuesta] = useState(false);
  const [nuevoTitulo, setNuevoTitulo] = useState('');
  const [nuevoTexto, setNuevoTexto] = useState('');
  const [nuevoVersiculo, setNuevoVersiculo] = useState('');
  const [nuevaImagenUrl, setNuevaImagenUrl] = useState('');
  const [propuestaActiva, setPropuestaActiva] = useState(null); // el que está siendo revisado por admin
  const [aprobandoPropuesta, setAprobandoPropuesta] = useState(false);
  const [rechazandoPropuesta, setRechazandoPropuesta] = useState(false);
  const [notificarPropuesta, setNotificarPropuesta] = useState(false);
  // Notificar push por-propuesta en la lista (mapa id → bool)
  const [notifPorPropuesta, setNotifPorPropuesta] = useState({});
  const [modoEdicion, setModoEdicion] = useState(false);
  const [tituloEditado, setTituloEditado] = useState('');
  const [textoEditado, setTextoEditado] = useState('');
  const [notificarPush, setNotificarPush] = useState(false);
  const [guardarEnBiblioteca, setGuardarEnBiblioteca] = useState(false);
  const [guardarPropuesta, setGuardarPropuesta] = useState(false);
  // Likes
  const [likeLoading, setLikeLoading] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [likesList, setLikesList] = useState([]);
  const [likesLoading, setLikesLoading] = useState(false);
  // Biblioteca de guardados
  const [pensamientosGuardados, setPensamientosGuardados] = useState([]);
  const [borrandoGuardado, setBorrandoGuardado] = useState(null);
  const [guardadoAbierto, setGuardadoAbierto] = useState(null);

  const isAdmin = user?.rol === 'admin';
  const isProfesorOAdmin = isAdmin || user?.rol === 'profesor';

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const activoPromise = axios.get(`${API}/pensamiento-biblico/activo`);
      const candidatoPromise = isAdmin
        ? axios.get(`${API}/admin/pensamiento-biblico/candidato`)
        : Promise.resolve({ data: { candidato: null } });
      const propuestasAdminPromise = isAdmin
        ? axios.get(`${API}/admin/pensamiento-biblico/propuestas`)
        : Promise.resolve({ data: [] });
      const misPropPromise = isProfesorOAdmin
        ? axios.get(`${API}/pensamiento-biblico/mis-propuestas`)
        : Promise.resolve({ data: [] });
      const guardadosPromise = axios.get(`${API}/pensamiento-biblico/guardados`);

      const [ra, rc, rp, rmp, rg] = await Promise.all([activoPromise, candidatoPromise, propuestasAdminPromise, misPropPromise, guardadosPromise]);
      setActivo(ra.data?.activo || null);
      setCandidato(rc.data?.candidato || null);
      setPropuestasAdmin(rp.data || []);
      setMisPropuestas(rmp.data || []);
      setPensamientosGuardados(rg.data?.guardados || []);
    } catch (error) {
      console.error('Error cargando Pensamientos Bíblicos:', error);
      toast.error('Error al cargar los Pensamientos Bíblicos');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isProfesorOAdmin]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Reset edit form whenever candidato changes
  useEffect(() => {
    if (candidato) {
      setTituloEditado(candidato.titulo_editado || candidato.titulo || '');
      setTextoEditado(candidato.texto_editado_html || candidato.texto_html || '');
    } else {
      setModoEdicion(false);
      setTituloEditado('');
      setTextoEditado('');
    }
  }, [candidato]);

  const refrescarCandidato = async () => {
    setRefrescando(true);
    try {
      const r = await axios.post(`${API}/admin/pensamiento-biblico/refrescar`);
      if (r.data?.status === 'created') {
        toast.success(`Nuevo pensamiento traído: "${r.data.titulo}"`);
      } else if (r.data?.status === 'unchanged') {
        toast.info('Ya tenés el más reciente de BibleTalk.tv (no hay nada nuevo)');
      } else if (r.data?.status === 'no_new') {
        toast.info(r.data.detail || 'BibleTalk.tv no tiene contenido nuevo esta vez');
      } else if (r.data?.status === 'error') {
        toast.error(r.data.detail || 'No se pudo obtener el pensamiento');
      }
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al refrescar');
    } finally {
      setRefrescando(false);
    }
  };

  const aprobar = async () => {
    if (!candidato) return;
    setAprobando(true);
    try {
      const body = { notificar_push: notificarPush, guardar_en_biblioteca: guardarEnBiblioteca };
      if (modoEdicion) {
        if (tituloEditado.trim()) body.titulo_editado = tituloEditado.trim();
        if (textoEditado.trim()) body.texto_editado_html = textoEditado.trim();
      }
      await axios.post(`${API}/admin/pensamiento-biblico/aprobar`, body);
      const partes = [];
      if (notificarPush) partes.push('notificado por push');
      if (guardarEnBiblioteca) partes.push('guardado en la biblioteca');
      const sufijo = partes.length ? ` (${partes.join(' y ')})` : '';
      toast.success(`✅ Pensamiento publicado${sufijo}`);
      setModoEdicion(false);
      setNotificarPush(false);
      setGuardarEnBiblioteca(false);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    } finally {
      setAprobando(false);
    }
  };

  const rechazar = async () => {
    if (!candidato) return;
    if (!window.confirm(`¿Rechazar "${candidato.titulo}"?\n\nSe descarta este pensamiento. Los estudiantes siguen viendo el activo actual. Al día siguiente llegará otro candidato.`)) return;
    setRechazando(true);
    try {
      await axios.post(`${API}/admin/pensamiento-biblico/rechazar`);
      toast.success('Pensamiento rechazado. Al día siguiente llegará otro.');
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    } finally {
      setRechazando(false);
    }
  };

  const retirarActivo = async () => {
    if (!activo) return;
    const titulo = activo.titulo_editado || activo.titulo || 'la publicación';
    if (!window.confirm(`¿Retirar la publicación "${titulo}"?\n\nLos estudiantes dejarán de verla. Después podés tocar "Buscar nuevo" para traer un candidato y publicarlo.`)) return;
    setRetirando(true);
    try {
      await axios.post(`${API}/admin/pensamiento-biblico/retirar-activo`);
      toast.success('Publicación retirada. Ya no la ven los estudiantes.');
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al retirar');
    } finally {
      setRetirando(false);
    }
  };

  const resetFormularioPropuesta = () => {
    setNuevoTitulo('');
    setNuevoTexto('');
    setNuevoVersiculo('');
    setNuevaImagenUrl('');
  };

  const enviarPropuesta = async () => {
    const titulo = nuevoTitulo.trim();
    const texto = nuevoTexto.trim();
    if (!titulo) { toast.error('El título es obligatorio'); return; }
    if (texto.length < 20) { toast.error('El texto es demasiado corto (mínimo 20 caracteres)'); return; }
    setEnviandoPropuesta(true);
    try {
      await axios.post(`${API}/pensamiento-biblico/proponer`, {
        titulo,
        texto,
        versiculo: nuevoVersiculo.trim() || null,
        imagen_url: nuevaImagenUrl.trim() || null,
      });
      toast.success('✅ Propuesta enviada al administrador para revisión');
      resetFormularioPropuesta();
      setShowCrearModal(false);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la propuesta');
    } finally {
      setEnviandoPropuesta(false);
    }
  };

  const aprobarPropuesta = async (propuesta, forceNotificar) => {
    if (!propuesta) return;
    // Si se llama desde el modal usa notificarPropuesta (state global del modal).
    // Si se llama desde el listado usa el checkbox individual de esa propuesta.
    const notif = typeof forceNotificar === 'boolean'
      ? forceNotificar
      : (propuestaActiva ? notificarPropuesta : Boolean(notifPorPropuesta[propuesta.id]));
    const guardar = propuestaActiva ? guardarPropuesta : false;
    setAprobandoPropuesta(true);
    try {
      await axios.post(`${API}/admin/pensamiento-biblico/propuesta/${propuesta.id}/aprobar`, {
        notificar_push: notif,
        guardar_en_biblioteca: guardar,
      });
      const partes = [];
      if (notif) partes.push('notificado por push');
      if (guardar) partes.push('guardado en la biblioteca');
      const sufijo = partes.length ? ` (${partes.join(' y ')})` : '';
      toast.success(`✅ "${propuesta.titulo}" publicado${sufijo}`);
      setPropuestaActiva(null);
      setNotificarPropuesta(false);
      setGuardarPropuesta(false);
      setNotifPorPropuesta({});
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al publicar la propuesta');
    } finally {
      setAprobandoPropuesta(false);
    }
  };

  const borrarGuardado = async (guardado) => {
    if (!guardado) return;
    const titulo = guardado.titulo_editado || guardado.titulo || 'este pensamiento';
    if (!window.confirm(`¿Eliminar "${titulo}" de la biblioteca?\n\nEsta acción no se puede deshacer.`)) return;
    setBorrandoGuardado(guardado.id);
    try {
      await axios.delete(`${API}/admin/pensamiento-biblico/guardados/${guardado.id}`);
      toast.success('Pensamiento eliminado de la biblioteca');
      setGuardadoAbierto(null);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setBorrandoGuardado(null);
    }
  };

  const rechazarPropuesta = async (propuesta) => {
    if (!propuesta) return;
    const motivo = window.prompt(
      `¿Rechazar la propuesta de ${propuesta.autor}?\n\nOpcional: escribí un motivo que el autor recibirá por notificación.\n(Dejá vacío para rechazar sin motivo)`,
      ''
    );
    if (motivo === null) return; // cancelled
    setRechazandoPropuesta(true);
    try {
      await axios.post(`${API}/admin/pensamiento-biblico/propuesta/${propuesta.id}/rechazar`, {
        motivo: motivo || null,
      });
      toast.success('Propuesta rechazada. El autor será notificado.');
      setPropuestaActiva(null);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar la propuesta');
    } finally {
      setRechazandoPropuesta(false);
    }
  };

  const eliminarMiPropuesta = async (propId) => {
    if (!window.confirm('¿Retirar esta propuesta? No podrás recuperarla.')) return;
    try {
      await axios.delete(`${API}/pensamiento-biblico/propuesta/${propId}`);
      toast.success('Propuesta retirada');
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al retirar');
    }
  };

  const displayTitulo = (doc) => (doc?.titulo_editado || doc?.titulo || 'Pensamiento Bíblico');
  const displayHtml = (doc) => (doc?.texto_editado_html || doc?.texto_html || '');

  const toggleLike = async () => {
    if (!activo || likeLoading) return;
    setLikeLoading(true);
    // optimistic update
    const prev = {
      liked: activo.liked_by_me,
      count: activo.likes_count || 0,
      preview: activo.likes_preview || [],
    };
    const newLiked = !prev.liked;
    const newCount = newLiked ? prev.count + 1 : Math.max(0, prev.count - 1);
    setActivo({ ...activo, liked_by_me: newLiked, likes_count: newCount });
    try {
      await axios.post(`${API}/pensamiento-biblico/toggle-like`);
      // refetch para sincronizar avatares
      const r = await axios.get(`${API}/pensamiento-biblico/activo`);
      if (r.data?.activo) setActivo(r.data.activo);
    } catch (error) {
      // revert
      setActivo({ ...activo, liked_by_me: prev.liked, likes_count: prev.count, likes_preview: prev.preview });
      toast.error('No se pudo registrar tu me gusta');
    } finally {
      setLikeLoading(false);
    }
  };

  const abrirModalLikes = async () => {
    setShowLikesModal(true);
    setLikesLoading(true);
    try {
      const r = await axios.get(`${API}/pensamiento-biblico/likes`);
      setLikesList(r.data?.likes || []);
    } catch (error) {
      toast.error('No se pudo cargar la lista de me gusta');
    } finally {
      setLikesLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500" data-testid="pensamientos-loading">
        <i className="fas fa-spinner fa-spin mr-2"></i>Cargando…
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="pensamientos-biblicos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <i className="fas fa-book-open text-indigo-600"></i>
            Pensamientos Bíblicos
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {isAdmin
              ? 'Revisá y publicá el pensamiento diario. Solo hay uno activo a la vez.'
              : 'Reflexiones bíblicas seleccionadas para tu edificación diaria.'}
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={refrescarCandidato}
            disabled={refrescando}
            variant="outline"
            data-testid="btn-refrescar-candidato"
          >
            <i className={`fas fa-sync-alt ${refrescando ? 'fa-spin' : ''} mr-2`}></i>
            Buscar nuevo
          </Button>
        )}
        {isProfesorOAdmin && (
          <Button
            onClick={() => setShowCrearModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="btn-crear-propuesta"
          >
            <i className="fas fa-plus mr-2"></i>
            Crear mi propio
          </Button>
        )}
      </div>

      {/* Admin: Candidato pendiente */}
      {isAdmin && candidato && (
        <Card className="border-2 border-amber-300 shadow-lg" data-testid="card-candidato">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <Badge className="bg-amber-500 text-white">Pendiente</Badge>
              <span>Nuevo pensamiento esperando tu aprobación</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {/* Meta */}
            <div className="flex flex-wrap gap-4 items-center text-sm text-gray-600">
              {candidato.autor && (
                <span className="flex items-center gap-2">
                  {candidato.autor_imagen && (
                    <img
                      src={candidato.autor_imagen}
                      alt={candidato.autor}
                      className="w-6 h-6 rounded-full object-cover border border-gray-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <span><i className="fas fa-user-tie mr-1 text-gray-400"></i>{candidato.autor}</span>
                </span>
              )}
              {candidato.versiculo && (
                <span><i className="fas fa-bible mr-1 text-gray-400"></i>{candidato.versiculo}</span>
              )}
              <a
                href={candidato.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:underline"
                data-testid="link-source-candidato"
              >
                <i className="fas fa-external-link-alt mr-1"></i>Ver original en BibleTalk.tv
              </a>
            </div>

            {/* Modo lectura o edición */}
            {!modoEdicion ? (
              <>
                {candidato.imagen_url && (
                  <img
                    src={candidato.imagen_url}
                    alt={candidato.titulo}
                    className="w-full max-h-56 object-cover rounded-lg border border-gray-200"
                    data-testid="candidato-imagen"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <div className="flex items-center gap-2">
                  {candidato.source_content_type && (
                    <Badge className={candidato.source_content_type === 'articulo' ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-emerald-100 text-emerald-800 border-emerald-300'}>
                      {candidato.source_content_type === 'articulo' ? '📄 Artículo' : '🌅 Devocional'}
                    </Badge>
                  )}
                  <h2 className="text-xl font-semibold text-gray-900" data-testid="candidato-titulo">
                    {displayTitulo(candidato)}
                  </h2>
                </div>
                {candidato.excerpt && (
                  <p className="italic text-gray-700 border-l-4 border-indigo-200 pl-3">
                    {candidato.excerpt}
                  </p>
                )}
                <div
                  className="text-gray-800 leading-relaxed [&_p]:mb-3 [&_blockquote]:border-l-4 [&_blockquote]:border-indigo-300 [&_blockquote]:pl-4 [&_blockquote]:py-1 [&_blockquote]:my-3 [&_blockquote]:bg-indigo-50/40 [&_blockquote]:italic [&_cite]:block [&_cite]:not-italic [&_cite]:text-sm [&_cite]:text-indigo-700 [&_cite]:font-semibold [&_cite]:mt-1 [&_sup]:text-xs [&_sup]:font-bold [&_sup]:text-indigo-600 [&_sup]:mr-1 [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-4 [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-3 [&_a]:text-indigo-600 [&_a]:underline"
                  data-testid="candidato-cuerpo"
                  dangerouslySetInnerHTML={{ __html: displayHtml(candidato) }}
                />
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 block">Título</label>
                  <Input
                    value={tituloEditado}
                    onChange={(e) => setTituloEditado(e.target.value)}
                    data-testid="input-titulo-editado"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 block">
                    Cuerpo HTML (podés ajustar términos, quitar párrafos, etc.)
                  </label>
                  <Textarea
                    value={textoEditado}
                    onChange={(e) => setTextoEditado(e.target.value)}
                    rows={12}
                    className="font-mono text-xs"
                    data-testid="textarea-texto-editado"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    <i className="fas fa-info-circle mr-1"></i>
                    Tip: podés eliminar frases doctrinalmente incompatibles antes de publicar.
                  </p>
                </div>
              </>
            )}

            {/* Opción de notificar */}
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer bg-indigo-50 border border-indigo-200 rounded p-3">
              <input
                type="checkbox"
                checked={notificarPush}
                onChange={(e) => setNotificarPush(e.target.checked)}
                data-testid="checkbox-notificar-push"
              />
              <span><i className="fas fa-bell mr-1 text-indigo-600"></i>Notificar por push a los estudiantes al publicar</span>
            </label>

            {/* Opción de guardar en biblioteca */}
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer bg-amber-50 border border-amber-200 rounded p-3">
              <input
                type="checkbox"
                checked={guardarEnBiblioteca}
                onChange={(e) => setGuardarEnBiblioteca(e.target.checked)}
                data-testid="checkbox-guardar-biblioteca"
              />
              <span><i className="fas fa-bookmark mr-1 text-amber-600"></i>Guardar en la biblioteca de Pensamientos (los usuarios podrán consultarlo hasta que lo borres)</span>
            </label>

            {/* Acciones */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
              {!modoEdicion ? (
                <Button variant="outline" onClick={() => setModoEdicion(true)} data-testid="btn-editar-candidato">
                  <i className="fas fa-edit mr-2"></i>Editar antes de publicar
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setModoEdicion(false)} data-testid="btn-cancelar-edicion">
                  <i className="fas fa-times mr-2"></i>Descartar edición
                </Button>
              )}
              <Button
                onClick={rechazar}
                disabled={rechazando || aprobando}
                variant="outline"
                className="text-red-600 hover:text-red-700 border-red-200"
                data-testid="btn-rechazar-candidato"
              >
                {rechazando ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-times mr-2"></i>}
                Rechazar
              </Button>
              <Button
                onClick={aprobar}
                disabled={aprobando || rechazando}
                className="bg-green-600 hover:bg-green-700 text-white ml-auto"
                data-testid="btn-aprobar-candidato"
              >
                {aprobando ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-check mr-2"></i>}
                Publicar {notificarPush && '+ Notificar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin: sin candidato */}
      {isAdmin && !candidato && (
        <Card className="border-dashed border-2 border-gray-200 bg-gray-50">
          <CardContent className="py-6 text-center text-gray-500">
            <i className="fas fa-inbox text-3xl mb-2 text-gray-300"></i>
            <p>No hay pensamientos de BibleTalk pendientes.</p>
              <p>Mañana a las 6:00 AM llegará el próximo. O tocá &quot;Buscar nuevo&quot; para forzar la búsqueda ahora.</p>
          </CardContent>
        </Card>
      )}

      {/* Admin: propuestas de profesores */}
      {isAdmin && propuestasAdmin.length > 0 && (
        <Card className="border-2 border-purple-300 shadow-lg" data-testid="card-propuestas-admin">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
            <CardTitle className="flex items-center gap-2 text-purple-900">
              <Badge className="bg-purple-500 text-white">{propuestasAdmin.length}</Badge>
              <span>Propuestas de Profesores esperando aprobación</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {propuestasAdmin.map((p) => (
              <div key={p.id} className="border border-gray-200 rounded-lg p-3 hover:bg-purple-50/50 transition" data-testid={`propuesta-item-${p.id}`}>
                <div className="flex items-start gap-3">
                  {p.autor_imagen ? (
                    <img
                      src={p.autor_imagen}
                      alt={p.autor}
                      className="w-10 h-10 rounded-full object-cover border border-gray-200 flex-shrink-0"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-user text-purple-500"></i>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                      <span className="font-medium text-gray-700">{p.autor}</span>
                      {p.creado_en && (
                        <span>· {new Date(p.creado_en).toLocaleString('es-UY', { timeZone: 'America/Montevideo', dateStyle: 'short', timeStyle: 'short' })}</span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{p.titulo}</h3>
                    {p.versiculo && <p className="text-xs text-indigo-700 mb-1"><i className="fas fa-bible mr-1"></i>{p.versiculo}</p>}
                    <div
                      className="text-sm text-gray-700 line-clamp-3 [&_p]:mb-1"
                      dangerouslySetInnerHTML={{ __html: p.texto_html || '' }}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer bg-indigo-50 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-100">
                    <input
                      type="checkbox"
                      checked={Boolean(notifPorPropuesta[p.id])}
                      onChange={(e) => setNotifPorPropuesta(prev => ({ ...prev, [p.id]: e.target.checked }))}
                      className="w-3.5 h-3.5"
                      data-testid={`checkbox-notif-lista-${p.id}`}
                    />
                    <i className="fas fa-bell text-indigo-600 text-xs"></i>
                    <span>Notificar por push</span>
                  </label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setPropuestaActiva(p); setNotificarPropuesta(Boolean(notifPorPropuesta[p.id])); }}
                    data-testid={`btn-revisar-propuesta-${p.id}`}
                  >
                    <i className="fas fa-eye mr-1"></i>Ver completo
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => rechazarPropuesta(p)}
                    disabled={rechazandoPropuesta}
                    className="text-red-600 border-red-200"
                    data-testid={`btn-rechazar-propuesta-${p.id}`}
                  >
                    <i className="fas fa-times mr-1"></i>Rechazar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => aprobarPropuesta(p)}
                    disabled={aprobandoPropuesta}
                    className="sm:ml-auto bg-green-600 hover:bg-green-700 text-white"
                    data-testid={`btn-aprobar-propuesta-${p.id}`}
                  >
                    <i className="fas fa-check mr-1"></i>Publicar{notifPorPropuesta[p.id] && ' + Notificar'}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Profesor: mis propuestas */}
      {isProfesorOAdmin && misPropuestas.length > 0 && (
        <Card className="border-gray-200" data-testid="card-mis-propuestas">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-100">
            <CardTitle className="flex items-center gap-2 text-slate-800 text-base">
              <i className="fas fa-list-check"></i>
              Mis propuestas ({misPropuestas.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-3">
            <div className="space-y-2">
              {misPropuestas.map((p) => {
                const isPending = p.estado === 'propuesta';
                const isActive = p.estado === 'activo';
                const isRejected = p.estado === 'rechazada';
                const label = isPending ? 'Pendiente' : isActive ? 'Publicado' : isRejected ? 'Rechazado' : p.estado;
                const badgeClass = isPending
                  ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                  : isActive
                    ? 'bg-green-100 text-green-800 border-green-300'
                    : 'bg-red-100 text-red-800 border-red-300';
                return (
                  <div key={p.id} className="flex items-center justify-between gap-2 border border-gray-100 rounded p-2 hover:bg-gray-50" data-testid={`mi-propuesta-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded border ${badgeClass}`}>{label}</span>
                        {p.creado_en && <span className="text-xs text-gray-500">{new Date(p.creado_en).toLocaleDateString('es-UY', { timeZone: 'America/Montevideo' })}</span>}
                      </div>
                      <div className="text-sm font-medium text-gray-800 truncate">{p.titulo}</div>
                      {isRejected && p.rechazado_motivo && (
                        <p className="text-xs text-red-600 mt-1 italic">Motivo: {p.rechazado_motivo}</p>
                      )}
                    </div>
                    {(isPending || isRejected) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500"
                        onClick={() => eliminarMiPropuesta(p.id)}
                        title="Retirar / eliminar"
                        data-testid={`btn-retirar-mi-propuesta-${p.id}`}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activo (visible para todos) */}
      {activo ? (
        <Card className="shadow-md" data-testid="card-activo">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardTitle className="flex items-center gap-2 text-indigo-900">
              <Badge className="bg-indigo-600 text-white">Publicado</Badge>
              <span className="text-base">Pensamiento Bíblico actual</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {activo.imagen_url && (
              <img
                src={activo.imagen_url}
                alt={displayTitulo(activo)}
                className="w-full max-h-72 object-cover rounded-lg border border-gray-200 shadow-sm"
                data-testid="activo-imagen"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <div className="flex flex-wrap gap-4 items-center text-sm text-gray-600">
              {activo.autor && (
                <span className="flex items-center gap-2">
                  {activo.autor_imagen && (
                    <img
                      src={activo.autor_imagen}
                      alt={activo.autor}
                      className="w-8 h-8 rounded-full object-cover border border-gray-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <span><i className="fas fa-user-tie mr-1 text-gray-400"></i>{activo.autor}</span>
                </span>
              )}
              {activo.versiculo && (
                <span><i className="fas fa-bible mr-1 text-gray-400"></i>{activo.versiculo}</span>
              )}
              {activo.publicado_en && (
                <span className="text-xs text-gray-500">
                  Publicado {new Date(activo.publicado_en).toLocaleDateString('es-UY', { timeZone: 'America/Montevideo' })}
                </span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-gray-900" data-testid="activo-titulo">
              {displayTitulo(activo)}
            </h2>
            {activo.excerpt && (
              <p className="italic text-gray-700 border-l-4 border-indigo-300 pl-3">
                {activo.excerpt}
              </p>
            )}
            <div
              className="text-gray-800 leading-relaxed text-base [&_p]:mb-4 [&_blockquote]:border-l-4 [&_blockquote]:border-indigo-400 [&_blockquote]:pl-4 [&_blockquote]:py-2 [&_blockquote]:my-4 [&_blockquote]:bg-indigo-50 [&_blockquote]:italic [&_blockquote]:rounded-r [&_cite]:block [&_cite]:not-italic [&_cite]:text-sm [&_cite]:text-indigo-800 [&_cite]:font-semibold [&_cite]:mt-2 [&_sup]:text-xs [&_sup]:font-bold [&_sup]:text-indigo-600 [&_sup]:mr-1 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mt-5 [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_a]:text-indigo-600 [&_a]:underline [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3"
              data-testid="activo-cuerpo"
              dangerouslySetInnerHTML={{ __html: displayHtml(activo) }}
            />
            <div className="pt-3 border-t border-gray-100 flex flex-wrap items-center gap-3">
              {/* Botón Me gusta + stack de avatares */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleLike}
                  disabled={likeLoading}
                  className={
                    activo.liked_by_me
                      ? 'border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:text-rose-800'
                      : 'border-gray-300 text-gray-600 hover:bg-rose-50 hover:text-rose-700'
                  }
                  data-testid="btn-like-pensamiento"
                >
                  <i className={`${activo.liked_by_me ? 'fas' : 'far'} fa-heart ${likeLoading ? 'animate-pulse' : ''} mr-1 ${activo.liked_by_me ? 'text-rose-600' : ''}`}></i>
                  <span>{activo.liked_by_me ? 'Te gusta' : 'Me gusta'}</span>
                  {activo.likes_count > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 text-xs font-semibold rounded-full bg-rose-100 text-rose-700">
                      {activo.likes_count}
                    </span>
                  )}
                </Button>

                {activo.likes_preview && activo.likes_preview.length > 0 && (
                  <button
                    type="button"
                    onClick={abrirModalLikes}
                    className="flex items-center -space-x-2 hover:scale-105 transition-transform"
                    title="Ver quiénes dieron me gusta"
                    data-testid="btn-ver-likes"
                  >
                    {activo.likes_preview.slice(0, 5).map((u) => (
                      <span
                        key={u.id}
                        className="inline-block w-7 h-7 rounded-full border-2 border-white shadow-sm overflow-hidden bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center"
                        title={u.nombre}
                      >
                        {u.foto_perfil ? (
                          <img src={u.foto_perfil} alt={u.nombre} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        ) : (
                          (u.nombre || '?').split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
                        )}
                      </span>
                    ))}
                    {activo.likes_count > 5 && (
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-white bg-gray-200 text-gray-700 text-[10px] font-bold">
                        +{activo.likes_count - 5}
                      </span>
                    )}
                  </button>
                )}
              </div>

              <p className="text-xs text-gray-500 italic flex-1 min-w-[200px]">
                <i className="fas fa-info-circle mr-1"></i>
                Fuente:{' '}
                <a
                  href={activo.source_url || 'https://bibletalk.tv'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                  data-testid="link-source-activo"
                >
                  BibleTalk.tv
                </a>{' '}
                — contenido bajo licencia Creative Commons BY-NC-SA 4.0. Reproducido con fines educativos.
              </p>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={retirarActivo}
                  disabled={retirando}
                  className="text-red-600 hover:text-red-700 border-red-200"
                  data-testid="btn-retirar-activo"
                >
                  {retirando ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-trash mr-2"></i>}
                  Retirar publicación
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-2 border-gray-200 bg-gray-50">
          <CardContent className="py-8 text-center text-gray-500">
            <i className="fas fa-book-open text-4xl mb-2 text-gray-300"></i>
            <p>Aún no se ha publicado ningún Pensamiento Bíblico.</p>
            {isAdmin && (
              <p className="text-xs mt-1">Aprobá el candidato pendiente para que aparezca acá.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal: lista completa de me gusta */}
      <Dialog open={showLikesModal} onOpenChange={(o) => { if (!o) { setShowLikesModal(false); setLikesList([]); } }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="modal-likes">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fas fa-heart text-rose-600"></i>
              <span>Les gustó este pensamiento</span>
              {activo?.likes_count ? (
                <Badge className="ml-auto bg-rose-100 text-rose-700 border-rose-200">{activo.likes_count}</Badge>
              ) : null}
            </DialogTitle>
          </DialogHeader>
          <div className="pt-2 space-y-1">
            {likesLoading ? (
              <div className="py-6 text-center text-gray-500 text-sm">
                <i className="fas fa-spinner fa-spin mr-2"></i>Cargando…
              </div>
            ) : likesList.length === 0 ? (
              <div className="py-6 text-center text-gray-500 text-sm">
                Todavía nadie dio me gusta. ¡Sé el primero!
              </div>
            ) : (
              likesList.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                  data-testid={`like-user-${u.id}`}
                >
                  <span className="w-10 h-10 rounded-full border border-gray-200 overflow-hidden bg-indigo-100 text-indigo-700 text-sm font-semibold flex items-center justify-center shrink-0">
                    {u.foto_perfil ? (
                      <img src={u.foto_perfil} alt={u.nombre} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      (u.nombre || '?').split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{u.nombre}</p>
                    {u.rol && (
                      <p className="text-xs text-gray-500 capitalize">{u.rol}</p>
                    )}
                  </div>
                  <i className="fas fa-heart text-rose-500 text-xs"></i>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Biblioteca de Pensamientos Guardados */}
      {pensamientosGuardados.length > 0 && (
        <Card className="mt-6 border-amber-200" data-testid="seccion-guardados">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-100">
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <i className="fas fa-bookmark text-amber-600"></i>
              Pensamientos Guardados
              <Badge className="ml-2 bg-amber-500 text-white">{pensamientosGuardados.length}</Badge>
            </CardTitle>
            <p className="text-xs text-amber-800 mt-1">
              {isAdmin
                ? 'Los usuarios pueden consultar estos pensamientos hasta que los elimines.'
                : 'Colección de pensamientos guardados por el administrador para consulta.'}
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {pensamientosGuardados.map((g) => (
                <div
                  key={g.id}
                  className="border border-amber-200 rounded-lg p-3 hover:shadow-md transition-all bg-white"
                  data-testid={`guardado-item-${g.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <button
                      onClick={() => setGuardadoAbierto(g)}
                      className="flex-1 text-left"
                      data-testid={`btn-abrir-guardado-${g.id}`}
                    >
                      <h4 className="font-semibold text-gray-800 line-clamp-2 hover:text-amber-700">
                        {g.titulo_editado || g.titulo}
                      </h4>
                      {g.versiculo && (
                        <p className="text-xs text-amber-700 mt-1">
                          <i className="fas fa-bible mr-1"></i>{g.versiculo}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Guardado el {new Date(g.guardado_en).toLocaleDateString('es-UY', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })}
                      </p>
                    </button>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => borrarGuardado(g)}
                        disabled={borrandoGuardado === g.id}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`btn-borrar-guardado-${g.id}`}
                      >
                        {borrandoGuardado === g.id
                          ? <i className="fas fa-spinner fa-spin"></i>
                          : <i className="fas fa-trash"></i>}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal: ver Pensamiento Guardado */}
      <Dialog open={!!guardadoAbierto} onOpenChange={(o) => { if (!o) setGuardadoAbierto(null); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="modal-ver-guardado">
          {guardadoAbierto && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Badge className="bg-amber-500 text-white"><i className="fas fa-bookmark mr-1"></i>Guardado</Badge>
                  <span className="text-gray-900">{guardadoAbierto.titulo_editado || guardadoAbierto.titulo}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                {guardadoAbierto.versiculo && (
                  <p className="text-sm text-amber-700 bg-amber-50 border-l-4 border-amber-400 p-2 rounded">
                    <i className="fas fa-bible mr-1"></i>{guardadoAbierto.versiculo}
                  </p>
                )}
                {guardadoAbierto.autor && (
                  <p className="text-xs text-gray-600">
                    <i className="fas fa-user-tie mr-1 text-gray-400"></i>{guardadoAbierto.autor}
                  </p>
                )}
                {guardadoAbierto.imagen_url && (
                  <img src={guardadoAbierto.imagen_url} alt="" className="w-full max-h-56 object-cover rounded" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
                <div
                  className="text-gray-800 leading-relaxed [&_p]:mb-3"
                  dangerouslySetInnerHTML={{ __html: guardadoAbierto.texto_editado_html || guardadoAbierto.texto_html || '' }}
                />
                <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
                  Guardado por {guardadoAbierto.guardado_por_nombre || 'el administrador'} el {new Date(guardadoAbierto.guardado_en).toLocaleDateString('es-UY', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Crear mi propio pensamiento */}
      <Dialog open={showCrearModal} onOpenChange={(o) => { if (!enviandoPropuesta) { setShowCrearModal(o); if (!o) resetFormularioPropuesta(); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="modal-crear-propuesta">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-800">
              <i className="fas fa-feather-alt"></i>
              Crear mi propio Pensamiento Bíblico
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Título *</label>
              <Input
                value={nuevoTitulo}
                onChange={(e) => setNuevoTitulo(e.target.value)}
                placeholder="Ej: La paz que sobrepasa todo entendimiento"
                maxLength={200}
                data-testid="input-nuevo-titulo"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Versículo (opcional)</label>
              <Input
                value={nuevoVersiculo}
                onChange={(e) => setNuevoVersiculo(e.target.value)}
                placeholder="Ej: Filipenses 4:6-7"
                data-testid="input-nuevo-versiculo"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Texto del pensamiento *</label>
              <Textarea
                value={nuevoTexto}
                onChange={(e) => setNuevoTexto(e.target.value)}
                placeholder={"Escribí acá tu reflexión.\n\nDejá una línea en blanco para separar párrafos.\nLos saltos de línea simples se respetan también."}
                rows={10}
                maxLength={8000}
                data-testid="textarea-nuevo-texto"
              />
              <p className="text-xs text-gray-500 mt-1 flex justify-between">
                <span>Doble Enter = nuevo párrafo. Mínimo 20 caracteres.</span>
                <span className={nuevoTexto.length > 7800 ? 'text-orange-600' : ''}>{nuevoTexto.length} / 8000</span>
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Imagen (opcional — URL)</label>
              <Input
                value={nuevaImagenUrl}
                onChange={(e) => setNuevaImagenUrl(e.target.value)}
                placeholder="https://ejemplo.com/imagen.jpg"
                data-testid="input-nueva-imagen"
              />
              <p className="text-xs text-gray-500 mt-1">
                <i className="fas fa-info-circle mr-1"></i>
                Si dejás vacío, se usará tu foto de perfil como avatar del pensamiento.
              </p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded p-2 text-xs text-purple-800">
              <i className="fas fa-shield-alt mr-1"></i>
              Tu propuesta se envía al administrador para revisión. Recibirás una notificación cuando la publique o rechace.
            </div>
            <div className="flex gap-2 pt-2 border-t border-gray-100">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowCrearModal(false); resetFormularioPropuesta(); }}
                disabled={enviandoPropuesta}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                onClick={enviarPropuesta}
                disabled={enviandoPropuesta || !nuevoTitulo.trim() || nuevoTexto.trim().length < 20}
                data-testid="btn-enviar-propuesta"
              >
                {enviandoPropuesta ? (
                  <><i className="fas fa-spinner fa-spin mr-2"></i>Enviando…</>
                ) : (
                  <><i className="fas fa-paper-plane mr-2"></i>Enviar al Admin</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Ver propuesta completa (admin) */}
      <Dialog open={!!propuestaActiva} onOpenChange={(o) => { if (!o && !aprobandoPropuesta && !rechazandoPropuesta) setPropuestaActiva(null); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="modal-revisar-propuesta">
          {propuestaActiva && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Badge className="bg-purple-500 text-white">Propuesta de profesor</Badge>
                  <span className="text-gray-900">{propuestaActiva.titulo}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  {propuestaActiva.autor_imagen && (
                    <img src={propuestaActiva.autor_imagen} alt={propuestaActiva.autor} className="w-8 h-8 rounded-full object-cover border" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  )}
                  <span><i className="fas fa-user-tie mr-1 text-gray-400"></i>{propuestaActiva.autor}</span>
                  {propuestaActiva.versiculo && <span><i className="fas fa-bible mr-1 text-gray-400"></i>{propuestaActiva.versiculo}</span>}
                </div>
                {propuestaActiva.imagen_url && (
                  <img src={propuestaActiva.imagen_url} alt="" className="w-full max-h-56 object-cover rounded" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
                <div
                  className="text-gray-800 leading-relaxed [&_p]:mb-3"
                  dangerouslySetInnerHTML={{ __html: propuestaActiva.texto_html || '' }}
                />
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer bg-indigo-50 border border-indigo-200 rounded p-2">
                  <input type="checkbox" checked={notificarPropuesta} onChange={(e) => setNotificarPropuesta(e.target.checked)} data-testid="checkbox-notif-propuesta" />
                  <span><i className="fas fa-bell mr-1 text-indigo-600"></i>Notificar por push a todos los estudiantes al publicar</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer bg-amber-50 border border-amber-200 rounded p-2">
                  <input type="checkbox" checked={guardarPropuesta} onChange={(e) => setGuardarPropuesta(e.target.checked)} data-testid="checkbox-guardar-propuesta" />
                  <span><i className="fas fa-bookmark mr-1 text-amber-600"></i>Guardar en la biblioteca de Pensamientos</span>
                </label>
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <Button variant="outline" onClick={() => setPropuestaActiva(null)} disabled={aprobandoPropuesta || rechazandoPropuesta}>
                    Cerrar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => rechazarPropuesta(propuestaActiva)}
                    disabled={aprobandoPropuesta || rechazandoPropuesta}
                    className="text-red-600 border-red-200"
                  >
                    <i className="fas fa-times mr-1"></i>Rechazar
                  </Button>
                  <Button
                    onClick={() => aprobarPropuesta(propuestaActiva)}
                    disabled={aprobandoPropuesta || rechazandoPropuesta}
                    className="ml-auto bg-green-600 hover:bg-green-700 text-white"
                    data-testid="btn-publicar-propuesta-modal"
                  >
                    <i className="fas fa-check mr-1"></i>Publicar {notificarPropuesta && '+ Notificar'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PensamientosBiblicos;
